/**
 * ScheduleCalendarView
 *
 * Reusable shell for every schedule calendar page (Job, Technician, Machine, …).
 * It owns:
 *   - the Card wrapper
 *   - ScheduleCalendarHeader (title bar + search/filter/sort + date nav + zoom)
 *   - a fixed-height scrollable content area with an optional loading overlay
 *   - ScheduleBarLegend at the bottom
 *
 * The actual calendar content (MonthlyCalendarGrid / ScheduleCalendarContent / …)
 * is passed as `children` — keeping this component unaware of domain logic.
 *
 * Usage
 * -----
 * ```tsx
 * <ScheduleCalendarView
 *   title="Job Schedule Calendar"
 *   isLoading={isLoading}
 *   headerAction={<Button>Export</Button>}
 *   {...allHeaderProps}
 * >
 *   {viewMode === "monthly" ? (
 *     <MonthlyCalendarGrid ... />
 *   ) : (
 *     <ScheduleCalendarContent ... />
 *   )}
 * </ScheduleCalendarView>
 * ```
 */

import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import ScheduleCalendarHeader from "./schedule-calendar-header";
import ScheduleBarLegend from "./schedule-bar-legend";
import type {
  FilterableAttribute,
  SortableAttribute,
  DynamicFilter,
  DynamicSort
} from "@/components/search-filter-bar/types";
import type { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface ScheduleCalendarViewProps {
  // ── Title bar ──────────────────────────────────────────────────────────────
  /** Page / module title shown in the title bar. Changes per active parent tab. */
  title: string;
  /** Optional right-side action in the title bar (e.g. Export button). */
  headerAction?: ReactNode;
  /** Optional control on the left of the search/filter row (e.g. month view Show picker). */
  toolbarStart?: ReactNode;

  // ── Loading ────────────────────────────────────────────────────────────────
  /** When true, renders a centered loading message instead of children. */
  isLoading?: boolean;
  /** Override the default "Loading schedule data…" message. */
  loadingMessage?: string;

  // ── Calendar content ───────────────────────────────────────────────────────
  /**
   * The calendar grid to render.
   * Typically `<MonthlyCalendarGrid>` or `<ScheduleCalendarContent>`.
   * Not rendered while isLoading is true.
   */
  children: ReactNode;

  // ── Header / toolbar passthrough ───────────────────────────────────────────
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filterableAttributes: FilterableAttribute[];
  dynamicFilters: DynamicFilter[];
  onAddFilter: (attribute: FilterableAttribute) => void;
  onUpdateFilter: (index: number, filter: DynamicFilter) => void;
  onRemoveFilter: (index: number) => void;
  onClearFilters: () => void;
  sortableAttributes: SortableAttribute[];
  dynamicSorts: DynamicSort[];
  onAddSort: (attribute: SortableAttribute) => void;
  onUpdateSort: (index: number, sort: DynamicSort) => void;
  onRemoveSort: (index: number) => void;
  onClearSorts: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Show the AM/PM availability legend (technician & machine tabs only, WOR-2187). */
  showAvailabilityLegend?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ScheduleCalendarView({
  // title bar
  title,
  headerAction,
  toolbarStart,
  // loading
  isLoading = false,
  loadingMessage = "Loading schedule data…",
  // content
  children,
  // header passthrough
  dateRange,
  onDateRangeChange,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filterableAttributes,
  dynamicFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  sortableAttributes,
  dynamicSorts,
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  viewMode,
  onViewModeChange,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  showAvailabilityLegend = false
}: ScheduleCalendarViewProps) {
  return (
    <div className="aero:space-y-4">
      <Card className="aero:py-0 aero:gap-0">
        {/* ── Toolbar: title bar + search/filter/sort + date nav + zoom ── */}
        <ScheduleCalendarHeader
          title={title}
          headerAction={headerAction}
          toolbarStart={toolbarStart}
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          filterableAttributes={filterableAttributes}
          dynamicFilters={dynamicFilters}
          onAddFilter={onAddFilter}
          onUpdateFilter={onUpdateFilter}
          onRemoveFilter={onRemoveFilter}
          onClearFilters={onClearFilters}
          sortableAttributes={sortableAttributes}
          dynamicSorts={dynamicSorts}
          onAddSort={onAddSort}
          onUpdateSort={onUpdateSort}
          onRemoveSort={onRemoveSort}
          onClearSorts={onClearSorts}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />

        {/* ── Calendar content area ── */}
        <CardContent className="aero:px-5 aero:pt-3 aero:p-0">
          <div className="aero:border aero:border-gray-200 aero:rounded-lg aero:overflow-hidden aero:max-h-[calc(100vh-300px)] aero:overflow-y-auto">
            {isLoading ? (
              <div className="aero:h-96 aero:flex aero:items-center aero:justify-center">
                <p className="aero:text-muted-foreground">{loadingMessage}</p>
              </div>
            ) : (
              children
            )}
          </div>
        </CardContent>

        {/* ── Bar legend ── */}
        <div className="aero:border-t aero:border-border/50 aero:px-5 aero:py-2 aero:flex aero:items-center">
          <ScheduleBarLegend showAvailability={showAvailabilityLegend} />
        </div>
      </Card>
    </div>
  );
}
